import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  FileText,
  Clock,
  Calendar,
  DollarSign,
  HelpCircle,
  AlertCircle,
  Download,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MainLayout from "@/components/layouts/MainLayout";

const ABNRegistration = () => {
  return (
    <>
      <Helmet>
        <title>ABN Registration | Numberz Insight</title>
        <meta
          name="description"
          content="Register for an Australian Business Number (ABN) with Numberz Insight. Professional assistance with ABN applications and business registration."
        />
        <meta
          name="keywords"
          content="ABN registration, Australian Business Number, business registration, tax agent, Numberz Insight"
        />
      </Helmet>

      <MainLayout>
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="relative bg-gradient-to-b from-primary/10 to-background dark:from-primary/5 dark:to-background py-16 md:py-24">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-left"
                >
                  <Badge
                    variant="outline"
                    className="mb-4 px-3 py-1 border-primary/30 text-primary"
                  >
                    <Clock className="w-3 h-3 mr-1 inline" /> Fast Processing
                  </Badge>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                    ABN Registration Made Simple
                  </h1>
                  <p className="text-xl text-muted-foreground mb-6">
                    Get your Australian Business Number quickly and hassle-free
                    with our expert assistance.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button size="lg" className="gap-2">
                      Start Application <ArrowRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="gap-2"
                      asChild
                    >
                      <Link to="/contact">Get Expert Advice</Link>
                    </Button>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="rounded-xl overflow-hidden shadow-xl"
                >
                  <img
                    src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                    alt="Business professional working on documents"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-16 bg-muted/30 dark:bg-muted/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Why Register for an ABN?
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  An Australian Business Number (ABN) is essential for
                  conducting business in Australia
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                      Business Identity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Establish your business identity and credibility with
                      customers, suppliers, and other businesses.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                      GST Registration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Required for GST registration if your business turnover
                      exceeds $75,000 annually.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                      Invoice Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Necessary for issuing valid tax invoices and claiming GST
                      credits on business purchases.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                      Government Interactions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Required for dealing with government agencies, accessing
                      grants, and participating in tenders.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                      Tax Benefits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Access to tax benefits and avoiding pay-as-you-go (PAYG)
                      tax on payments from other businesses.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                      Business Banking
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Often required by banks for opening business accounts and
                      accessing business financial products.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Process Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Our ABN Registration Process
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  We make the ABN registration process simple and efficient
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">1</span>
                    </div>
                    <CardTitle>Consultation</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      Initial consultation to understand your business needs and
                      structure.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">2</span>
                    </div>
                    <CardTitle>Documentation</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      Collect and prepare all necessary documentation for your
                      application.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">3</span>
                    </div>
                    <CardTitle>Submission</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      Submit your application to the Australian Business
                      Register (ABR).
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">4</span>
                    </div>
                    <CardTitle>Confirmation</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      Receive your ABN and guidance on next steps for your
                      business.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-12 text-center">
                <Button size="lg" className="gap-2">
                  Start Your ABN Application <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16 bg-muted/30 dark:bg-muted/10">
            <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-xl text-muted-foreground">
                  Common questions about ABN registration
                </p>
              </div>

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger>
                    Who is eligible for an ABN?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground mb-2">
                      You're eligible for an ABN if you're:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>
                        Carrying on or starting an enterprise in Australia
                      </li>
                      <li>
                        Making supplies connected with Australia's indirect tax
                        zone
                      </li>
                      <li>
                        A Corporations Act company registered in Australia
                      </li>
                    </ul>
                    <p className="text-muted-foreground mt-2">
                      Hobby activities generally don't qualify as enterprises
                      for ABN purposes.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger>
                    How long does it take to get an ABN?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      With our service, most ABN applications are processed
                      within 1-2 business days. If your application is
                      straightforward, you may receive your ABN immediately upon
                      submission. More complex applications may take up to 28
                      days for the ATO to process.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger>
                    What documents do I need for an ABN application?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground mb-2">
                      Required documents typically include:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>Tax File Number (TFN)</li>
                      <li>
                        Identification documents (driver's license, passport,
                        etc.)
                      </li>
                      <li>
                        Business name registration details (if applicable)
                      </li>
                      <li>Company registration details (for companies)</li>
                      <li>Partnership or trust deed (if applicable)</li>
                      <li>Details about your business activities</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger>
                    Is there a fee for ABN registration?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      There is no government fee for applying for an ABN.
                      However, our professional service fees apply for our
                      assistance with the application process, ensuring it's
                      completed correctly and efficiently. Please contact us for
                      our current fee structure.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5">
                  <AccordionTrigger>
                    Do I need to renew my ABN?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      No, an ABN does not need to be renewed. However, you must
                      update your ABN details if your business circumstances
                      change, such as changes to business structure, contact
                      details, or if you cease business operations. We can
                      assist with maintaining your ABN records.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6">
                  <AccordionTrigger>Can I have multiple ABNs?</AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      An individual can only have one ABN for all their business
                      activities. However, if you operate different business
                      structures (e.g., as a sole trader and also as a company
                      director), each entity may have its own ABN. We can advise
                      on the most appropriate structure for your situation.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-primary/5 dark:bg-primary/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">
                    Ready to Register Your ABN?
                  </h2>
                  <p className="text-xl text-muted-foreground mb-6">
                    Our team of tax professionals is ready to help you with your
                    ABN registration. Contact us today to get started.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Expert guidance throughout the process</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Fast and efficient service</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Ongoing support for your business</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Card>
                    <CardHeader>
                      <CardTitle>Start Your ABN Application</CardTitle>
                      <CardDescription>
                        Fill out the form below and one of our tax professionals
                        will contact you shortly.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <Button className="w-full" size="lg">
                          Start Application
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          size="lg"
                          asChild
                        >
                          <Link to="/contact">Contact Us</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Additional Resources
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Helpful information about ABN registration and business
                  operations
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      ABN Lookup
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Verify ABN details and GST registration status of
                      businesses.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://abr.business.gov.au/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit ABN Lookup{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Business Registration Guide
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Comprehensive guide to registering a business in
                      Australia.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://business.gov.au/registrations/register-a-business"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Read Guide <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Tax Obligations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Information about tax obligations for businesses in
                      Australia.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://www.ato.gov.au/business/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        ATO Business Portal{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </main>
      </MainLayout>
    </>
  );
};

export default ABNRegistration;
